/**
 * REST client for the play-runebound Worker (`@play/protocol` rest.ts lists the endpoints). Every call
 * carries the Supabase access token; responses are parsed with the protocol's zod schemas (a trust
 * boundary) and failures become an {@link ApiFailure} with the server's error code.
 */
import {
  ApiErrorSchema,
  type BotMatchRequest,
  BotMatchRequestSchema,
  BotPresetSchema,
  type CreateRoomRequest,
  MatchCreatedSchema,
  MatchSummarySchema,
  QueueStatusSchema,
  ReplayFileSchema,
  RoomViewSchema,
  TicketResponseSchema,
  type QueueJoinRequest,
} from '@play/protocol';
import type { z } from 'zod';
import { reportRejectedSession } from '../auth/session-expiry';
import { env } from './env';
import { accessToken } from './supabase';

/** Error codes the client distinguishes: the server's, plus transport problems. */
export type ApiFailureCode = z.infer<typeof ApiErrorSchema>['error']['code'] | 'network' | 'badResponse';

/** A failed API call. */
export class ApiFailure extends Error {
  override readonly name = 'ApiFailure';
  constructor(
    readonly code: ApiFailureCode,
    message: string,
    readonly status: number,
    readonly issues: { code: string; message: string }[] = [],
  ) {
    super(message);
  }
}

/** Absolute URL of a Worker path (same origin unless `VITE_API_ORIGIN` is set). */
export function apiUrl(path: string): string {
  return `${env.apiOrigin}${path}`;
}

/** WebSocket URL for a Worker path (`/match/<id>/ws?ticket=…`). */
export function wsUrl(path: string): string {
  const base = env.apiOrigin || window.location.origin;
  return base.replace(/^http/, 'ws') + path;
}

async function call<T>(method: string, path: string, schema: z.ZodType<T>, body?: unknown): Promise<T> {
  const token = await accessToken();
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : null,
    });
  } catch {
    throw new ApiFailure('network', 'No connection to Runebound Play.', 0);
  }
  const json: unknown = await res.json().catch(() => null);
  // The server refused the token we sent: the session is over (expired or revoked).
  if (res.status === 401 && token) reportRejectedSession();
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(json);
    if (parsed.success) {
      const { code, message, issues } = parsed.data.error;
      throw new ApiFailure(code, message, res.status, issues ?? []);
    }
    throw new ApiFailure(res.status === 401 ? 'unauthorized' : 'server', `Request failed (${res.status}).`, res.status);
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ApiFailure('badResponse', 'Unexpected answer from the server.', res.status);
  return parsed.data;
}

/** The Worker API used by the lobby, rooms, matches and replays. */
export const api = {
  joinQueue: (req: QueueJoinRequest) => call('POST', '/api/queue', QueueStatusSchema, req),
  queueStatus: () => call('GET', '/api/queue', QueueStatusSchema),
  leaveQueue: () => call('DELETE', '/api/queue', QueueStatusSchema),
  createRoom: (req: CreateRoomRequest) => call('POST', '/api/rooms', RoomViewSchema, req),
  room: (code: string) => call('GET', `/api/rooms/${encodeURIComponent(code)}`, RoomViewSchema),
  joinRoom: (code: string, deckId: string) =>
    call('POST', `/api/rooms/${encodeURIComponent(code)}/join`, RoomViewSchema, { deckId }),
  roomDeck: (code: string, deckId: string) =>
    call('POST', `/api/rooms/${encodeURIComponent(code)}/deck`, RoomViewSchema, { deckId }),
  startRoom: (code: string) => call('POST', `/api/rooms/${encodeURIComponent(code)}/start`, MatchCreatedSchema),
  leaveRoom: (code: string) => call('POST', `/api/rooms/${encodeURIComponent(code)}/leave`, RoomViewSchema),
  botDecks: () => call('GET', '/api/bot-decks', BotPresetSchema.array()),
  botMatch: (req: BotMatchRequest) =>
    call('POST', '/api/bot-matches', MatchCreatedSchema, BotMatchRequestSchema.parse(req)),
  ticket: (matchId: string) => call('POST', `/api/match/${encodeURIComponent(matchId)}/ticket`, TicketResponseSchema),
  match: (matchId: string) => call('GET', `/api/matches/${encodeURIComponent(matchId)}`, MatchSummarySchema),
  replay: (replayId: string) => call('GET', `/api/replays/${encodeURIComponent(replayId)}`, ReplayFileSchema),
  sharedReplay: (token: string) => call('GET', `/api/replays/s/${encodeURIComponent(token)}`, ReplayFileSchema),
};

/** One-line, user-facing copy for an API failure. */
export function failureMessage(error: unknown): string {
  if (error instanceof ApiFailure) {
    switch (error.code) {
      case 'network':
        return 'No connection. Check your network and try again.';
      case 'unauthorized':
        return 'Your session expired, sign in again.';
      case 'tierDisabled':
        return 'That rules tier is switched off right now.';
      case 'deckInvalid':
        return error.issues[0]?.message ?? 'That deck is not legal for this format.';
      case 'blocked':
        return 'You can’t play with this player.';
      case 'rateLimited':
        return 'Too many requests. Wait a moment and try again.';
      case 'notFound':
        return 'Not found. The link may have expired.';
      case 'forbidden':
      case 'invalidRequest':
      case 'conflict':
      case 'server':
      case 'badResponse':
        return error.message;
    }
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
